/**
 * CoverageSelector Component
 */

import { ExpandMore } from "@mui/icons-material";
import { Alert, Button, Menu, MenuItem, Snackbar } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { schemeAiPalette } from "@platform/design-system";
import {
  COVERAGE_COMPLETION_STATUSES,
  CoverageTypeEnum,
} from "@platform/types";
import { RfpCoverage, coverageTypeToLabel } from "@platform/plan-grid-core";

import { useGetAvailableCoverages } from "../api";

import type { Theme } from "@mui/material/styles";

// Collapses the backend's incomplete and awaiting-input into "Incomplete"
const toStatusLabel = (status: string | null | undefined): string => {
  if (!status) return "";
  return status === COVERAGE_COMPLETION_STATUSES.complete
    ? "Complete"
    : "Incomplete";
};

// Same theme chips as the Build tab's status chip; other statuses use the default chip
const STATUS_CHIPS: Partial<
  Record<string, keyof Theme["palette"]["ai"]["chips"]>
> = {
  [COVERAGE_COMPLETION_STATUSES.complete]: "success",
  [COVERAGE_COMPLETION_STATUSES.awaitingInput]: "primary",
};

export interface CoverageSelectorProps {
  ecoRfpId: string;
  selectedCoverage?: RfpCoverage;
  onCoverageSelect: (coverageType: CoverageTypeEnum) => void;
  size?: "small" | "medium" | "large";
  variant?: "text" | "outlined" | "contained";
}

export const CoverageSelector: React.FC<CoverageSelectorProps> = ({
  ecoRfpId,
  selectedCoverage,
  onCoverageSelect,
  size = "small",
  variant = "outlined",
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const [errorOpen, setErrorOpen] = useState(false);

  // Fetch available coverage types directly in this component
  const {
    data: availableCoverageTypes = [],
    statusByType,
    isLoading,
    isError,
    error,
  } = useGetAvailableCoverages(ecoRfpId);

  // Show error toast when API request fails
  useEffect(() => {
    if (isError && error) {
      setErrorOpen(true);
    }
  }, [isError, error]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleItemClick = (coverageType: CoverageTypeEnum): void => {
    onCoverageSelect(coverageType);
    handleMenuClose();
  };

  const menuItems = useMemo(() => {
    const currentCoverageType = selectedCoverage?.type;

    if (availableCoverageTypes.length > 0) {
      return availableCoverageTypes.map((type) => ({
        type,
        label: [
          coverageTypeToLabel(type),
          toStatusLabel(statusByType.get(type)),
        ]
          .filter(Boolean)
          .join(" - "),
        selected: type === currentCoverageType,
      }));
    }

    // While loading or if API fails, show at least the current coverage if available
    if (currentCoverageType) {
      return [
        {
          type: currentCoverageType,
          label: coverageTypeToLabel(currentCoverageType),
          selected: true,
        },
      ];
    }

    return [];
  }, [availableCoverageTypes, statusByType, selectedCoverage]);

  const selectedItem = menuItems.find((item) => item.selected) || menuItems[0];
  const buttonLabel =
    isLoading && !selectedItem
      ? "Loading..."
      : selectedItem
        ? coverageTypeToLabel(selectedItem.type)
        : "Select Coverage";

  return (
    <>
      {/* TODO(ds-button): needs manual review — variant is a runtime prop, no single DS variant maps to it */}
      <Button
        variant={variant}
        size={size}
        endIcon={<ExpandMore />}
        onClick={handleMenuOpen}
        disabled={isLoading && menuItems.length === 0}
        color="brandPrimary"
      >
        {buttonLabel}
      </Button>
      <Menu anchorEl={anchorEl} open={isOpen} onClose={handleMenuClose}>
        {menuItems.map((item) => (
          <MenuItem
            key={item.type}
            selected={item.selected}
            onClick={() => handleItemClick(item.type)}
            sx={(theme) => {
              const chip =
                STATUS_CHIPS[statusByType.get(item.type) ?? ""] ?? "default";
              return {
                "&:hover, &.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible":
                  {
                    bgcolor: schemeAiPalette(theme).chips[chip].backgroundColor,
                  },
              };
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorOpen(false)}
          severity="error"
          variant="filled"
        >
          Failed to load available coverages.{" "}
          {error?.message || "Please try again."}
        </Alert>
      </Snackbar>
    </>
  );
};

CoverageSelector.displayName = "CoverageSelector";
export default CoverageSelector;
